import React, { useEffect, useState } from 'react';
import { Download, ExternalLink, Mail, Pencil } from 'lucide-react';
import api from '../services/api';
import Modal from './Modal';
import { formatMonto, formatFecha } from '../utils/format';
import { tipoPersona, valoresIslr } from '../utils/retenciones';

// ============================================================
// Comprobante de retención de compra abierto desde Contabilidad →
// Retenciones: vista del documento, PDF (ver o descargar), envío por correo
// y edición de todo lo que el sistema pudo generar mal: número, fecha, base,
// %, monto retenido y, en ISLR, concepto y sustraendo. El monto se recalcula
// con base y % hasta que se escribe a mano. El servidor reajusta la factura;
// un número repetido (mismo tipo, misma empresa) se rechaza.
// ============================================================

const TITULO = { iva: 'Retención de IVA', islr: 'Retención de ISLR', municipal: 'Retención municipal' };
const r2 = (n) => Math.round(Number(n) * 100) / 100;

async function blobDe(url) {
  const resp = await api.get(url, { responseType: 'blob' });
  return { resp, href: window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })) };
}

export default function ComprobanteRetencion({ retencionId, onCerrar, onGuardado }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null); // null = solo vista
  const [conceptos, setConceptos] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const cargar = () => api.get(`/compras/retenciones/${retencionId}`)
    .then(({ data }) => setD(data))
    .catch((err) => setError(err.response?.data?.error || 'No se pudo abrir el comprobante'));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [retencionId]);

  const r = d?.retencion;
  const mon = d?.factura.moneda === 'USD' ? '$ ' : 'Bs ';

  function editar() {
    setError('');
    if (r.tipo === 'islr' && conceptos.length === 0) {
      api.get('/retencion-islr-codigos').then(({ data }) => setConceptos(data.filter((c) => c.activo !== 0 && c.activo !== false))).catch(() => {});
    }
    setForm({
      numero_comprobante: r.numero_comprobante,
      fecha: String(r.fecha).slice(0, 10),
      base_imponible: String(Number(r.base_imponible)),
      monto: '', // vacío = calculado con base y %
      porcentaje: r.porcentaje != null ? String(Number(r.porcentaje)) : '',
      islr_codigo_id: r.islr_codigo_id ? String(r.islr_codigo_id) : '',
      sustraendo: r.sustraendo != null ? String(Number(r.sustraendo)) : ''
    });
  }

  function cambiarConcepto(id) {
    const cod = conceptos.find((c) => String(c.id) === id);
    const v = valoresIslr(cod, tipoPersona(d.proveedor.rif));
    setForm({
      ...form,
      islr_codigo_id: id,
      porcentaje: v.porcentaje != null ? String(Number(v.porcentaje)) : '',
      sustraendo: v.sustraendo != null ? String(Number(v.sustraendo)) : '0'
    });
  }

  // Mismo cálculo que el servidor, para ver el monto antes de guardar.
  const montoCalculado = form
    ? r2(Math.max((Number(form.base_imponible) || 0) * (Number(form.porcentaje) || 0) / 100 - (r.tipo === 'islr' ? Number(form.sustraendo) || 0 : 0), 0))
    : null;
  const montoNuevo = form && form.monto !== '' ? r2(form.monto) : montoCalculado;

  async function guardar(e) {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      await api.put(`/compras/retenciones/${retencionId}`, {
        numero_comprobante: form.numero_comprobante,
        fecha: form.fecha,
        base_imponible: form.base_imponible,
        porcentaje: form.porcentaje,
        monto: form.monto !== '' ? form.monto : undefined,
        ...(r.tipo === 'islr' ? { islr_codigo_id: form.islr_codigo_id || undefined, sustraendo: form.sustraendo } : {})
      });
      setForm(null);
      await cargar();
      onGuardado?.();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el comprobante');
    } finally {
      setGuardando(false);
    }
  }

  async function verPdf() {
    const ventana = window.open('', '_blank');
    try {
      const { href } = await blobDe(`/compras/retenciones/${retencionId}/pdf?ver=1`);
      if (ventana) ventana.location.href = href; else window.location.href = href;
    } catch {
      if (ventana) ventana.close();
      alert('No se pudo abrir el PDF');
    }
  }

  async function descargarPdf() {
    try {
      const { href } = await blobDe(`/compras/retenciones/${retencionId}/pdf`);
      const a = document.createElement('a');
      a.href = href;
      a.download = `Retencion-${r.tipo.toUpperCase()}-${r.numero_comprobante}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
    } catch {
      alert('No se pudo descargar el PDF');
    }
  }

  async function enviar() {
    const email = window.prompt(`Enviar el comprobante N° ${r.numero_comprobante} en PDF a:`, d.proveedor.email || '');
    if (!email) return;
    try {
      await api.post(`/compras/retenciones/${retencionId}/enviar`, { email: email.trim() });
      alert(`Comprobante enviado a ${email.trim()}`);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo enviar el correo');
    }
  }

  const fila = (label, valor) => (
    <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-right text-slate-800">{valor}</span>
    </div>
  );

  return (
    <Modal title={r ? `${TITULO[r.tipo]} N° ${r.numero_comprobante}` : 'Comprobante de retención'} onClose={onCerrar} maxWidth="max-w-lg">
      {!d && !error && <p className="text-sm text-slate-400">Cargando…</p>}
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {d && !form && (
        <div className="space-y-4">
          <div className="rounded-lg border p-4 text-sm">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="font-semibold text-slate-800">{d.empresa.nombre}</p>
                {d.empresa.rfc_nit && <p className="text-xs text-slate-500">RIF {d.empresa.rfc_nit}</p>}
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-slate-400">Comprobante</p>
                <p className="font-semibold text-slate-800">{r.numero_comprobante}</p>
                <p className="text-xs text-slate-500">{formatFecha(r.fecha)}</p>
              </div>
            </div>
            {fila('Proveedor', <>{d.proveedor.nombre}{d.proveedor.rif ? <span className="text-slate-400"> · {d.proveedor.rif}</span> : ''}</>)}
            {fila('Factura', <>N° {d.factura.numero}{d.factura.numero_control ? ` · control ${d.factura.numero_control}` : ''} · {formatFecha(d.factura.fecha)}</>)}
            {r.tipo === 'islr' && fila('Concepto', r.islr_concepto || '—')}
            {fila(r.tipo === 'iva' ? 'IVA de la factura' : 'Base imponible', `${mon}${formatMonto(r.base_imponible)}`)}
            {fila('% retenido', r.porcentaje != null ? `${formatMonto(r.porcentaje)} %` : '—')}
            {r.tipo === 'islr' && fila('Sustraendo', `${mon}${formatMonto(r.sustraendo || 0)}`)}
            <div className="flex justify-between gap-4 pt-2 font-semibold text-slate-800">
              <span>Monto retenido</span>
              <span>{mon}{formatMonto(r.monto)}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <button type="button" onClick={verPdf} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm text-slate-600 hover:bg-slate-50">
              <ExternalLink size={15} /> Ver PDF
            </button>
            <button type="button" onClick={descargarPdf} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm text-slate-600 hover:bg-slate-50">
              <Download size={15} /> Descargar PDF
            </button>
            <button type="button" onClick={enviar} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm text-slate-600 hover:bg-slate-50">
              <Mail size={15} /> Enviar
            </button>
            <button type="button" onClick={editar} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary-600 text-sm text-white hover:bg-primary-700">
              <Pencil size={15} /> Editar
            </button>
          </div>
        </div>
      )}

      {d && form && (
        <form onSubmit={guardar} className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">N° de comprobante</label>
              <input
                value={form.numero_comprobante} maxLength={20} required
                onChange={(e) => setForm({ ...form, numero_comprobante: e.target.value })}
                className="border rounded-md px-2 py-1.5 w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Fecha</label>
              <input
                type="date" value={form.fecha} required
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className="border rounded-md px-2 py-1.5 w-full"
              />
            </div>
            {r.tipo === 'islr' && (
              <div className="col-span-2">
                <label className="block text-xs text-slate-500 mb-1">Concepto ISLR</label>
                <select value={form.islr_codigo_id} onChange={(e) => cambiarConcepto(e.target.value)} className="border rounded-md px-2 py-1.5 w-full">
                  {!conceptos.some((c) => String(c.id) === form.islr_codigo_id) && (
                    <option value={form.islr_codigo_id}>{r.islr_concepto || 'Concepto actual'}</option>
                  )}
                  {conceptos.map((c) => <option key={c.id} value={String(c.id)}>{c.codigo ? `${c.codigo} · ` : ''}{c.concepto}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-500 mb-1">% retenido</label>
              {r.tipo === 'iva' ? (
                <select value={form.porcentaje} onChange={(e) => setForm({ ...form, porcentaje: e.target.value })} className="border rounded-md px-2 py-1.5 w-full">
                  <option value="75">75%</option>
                  <option value="100">100%</option>
                </select>
              ) : (
                <input
                  type="number" step="0.01" min="0" max="100" value={form.porcentaje} required
                  onChange={(e) => setForm({ ...form, porcentaje: e.target.value })}
                  className="border rounded-md px-2 py-1.5 w-full"
                />
              )}
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">{r.tipo === 'iva' ? 'IVA de la factura (base)' : 'Base imponible'}</label>
              <input
                type="number" step="0.01" min="0" value={form.base_imponible} required
                onChange={(e) => setForm({ ...form, base_imponible: e.target.value })}
                className="border rounded-md px-2 py-1.5 w-full"
              />
            </div>
            {r.tipo === 'islr' && (
              <div>
                <label className="block text-xs text-slate-500 mb-1">Sustraendo</label>
                <input
                  type="number" step="0.01" min="0" value={form.sustraendo}
                  onChange={(e) => setForm({ ...form, sustraendo: e.target.value })}
                  className="border rounded-md px-2 py-1.5 w-full"
                />
              </div>
            )}
          </div>
          <div className="rounded-md bg-slate-50 px-3 py-2 text-slate-600 space-y-1">
            <div className="flex items-center gap-2">
              <label className="whitespace-nowrap">Monto retenido</label>
              <input
                type="number" step="0.01" min="0" value={form.monto}
                placeholder={formatMonto(montoCalculado)}
                onChange={(e) => setForm({ ...form, monto: e.target.value })}
                className="border rounded-md px-2 py-1 w-36 text-right bg-white"
              />
              {form.monto !== '' && (
                <button type="button" onClick={() => setForm({ ...form, monto: '' })} className="text-xs text-primary-600 hover:underline">
                  Volver al cálculo
                </button>
              )}
            </div>
            <p className="text-xs text-slate-400">
              {form.monto === ''
                ? `Calculado: base × %${r.tipo === 'islr' ? ' − sustraendo' : ''} = ${mon}${formatMonto(montoCalculado)}. Escribilo si el correcto es otro.`
                : 'Monto escrito a mano: se guarda tal cual.'}
              {montoNuevo !== r2(r.monto) && ` Antes: ${mon}${formatMonto(r.monto)}.`} Al guardar, el saldo de la factura y el libro se actualizan.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setForm(null); setError(''); }} className="px-4 py-2 rounded-md text-slate-600 hover:bg-slate-100">Cancelar</button>
            <button type="submit" disabled={guardando} className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-60">
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
